/**
 * 智擎智能体平台认证接口
 * 用于获取并存储 tianzhiToken（agent token）
 * 适配自 develop_chat_front 项目的认证流程
 */
import axios from 'axios'
import Cookies from 'js-cookie'
import JSEncrypt from 'jsencrypt'
import { getToken } from '@/utils/auth'
import { Message } from 'element-ui'
import clientRequest from './clientRequest'
import portalRequest from '@/utils/request'
import { addSphereUser } from './sphereUser'

// 创建一个独立的 axios 实例，不携带门户的 Authorization 头
const agentRequest = axios.create({
  timeout: 20000,
  withCredentials: false
})

// 请求拦截：/intelligent-agent 路径不带 Bearer，其余路径带 Bearer
agentRequest.interceptors.request.use((config) => {
  const tianzhiToken = getCurrentTianzhiToken()
  if (tianzhiToken && !config.headers.Authorization) {
    if (config.url && config.url.includes('/intelligent-agent/')) {
      config.headers.Authorization = tianzhiToken
    } else {
      config.headers.Authorization = 'Bearer ' + tianzhiToken
    }
  }
  return config
})

// 401 重试状态
let _isRefreshingToken = false
let _pendingRequests = []

/**
 * 刷新 tianzhiToken（与 fetchAgentToken 逻辑一致）
 */
async function refreshTianzhiToken() {
  const token = getToken()
  if (!token) throw new Error('未获取到门户 token')

  // 1. 获取 RSA 公钥
  const pubRes = await getPublicKeyApi()
  const publicKey = pubRes.data?.public_key || pubRes.public_key
  if (!publicKey) throw new Error('获取公钥失败')

  // 2. 加密密码
  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(publicKey)
  const password = encrypt.encrypt('Cosmoplat@2024')
  if (!password) throw new Error('密码加密失败')

  // 3. 调用注册接口获取新 token
  const regRes = await newNewRegistAgentSystemApi(password, token)
  const regData = regRes.data || regRes
  if (!regData || !regData.access_token) {
    throw new Error('注册接口未返回 access_token')
  }

  // 4. 存储新 token（仅存 cookie，key 从环境变量读取）
  const agentTokenKey =
    process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
  const accessToken = regData.access_token
  Cookies.set(agentTokenKey, accessToken, {
    path: '/',
    SameSite: 'None',
    Secure: true
  })

  if (regData.third_user_name) {
    localStorage.setItem('cosmo_claw_user_name', regData.third_user_name)
  }
  if (regData.platform_role) {
    localStorage.setItem('platform_role', regData.platform_role)
  }

  return accessToken
}

/**
 * 获取当前存储的 tianzhiToken
 */
function getCurrentTianzhiToken() {
  const agentTokenKey =
    process.env.VUE_APP_WS_TOKEN_KEY || 'tianzhi_test_agent_token'
  return Cookies.get(agentTokenKey) || ''
}

/**
 * 从错误对象中提取可读的错误消息
 */
function getErrorMessage(err) {
  if (typeof err === 'string') return err
  // HTTP 错误（如 500）：真实信息在 response.data 中
  if (err?.response?.data?.message) return err.response.data.message
  if (err?.response?.data?.msg) return err.response.data.msg
  if (err?.response?.data?.error) return err.response.data.error
  if (err?.response?.statusText) return err.response.statusText
  // 业务错误响应体：{ code: xxx, message: '...' } 或 { code: xxx, msg: '...' }
  if (err?.message) return err.message
  if (err?.msg) return err.msg
  return '请求失败，请稍后重试'
}

// 缓存工作区 ID（从 /workspaces/my 接口获取）
let _cachedWorkspaceId = null

/**
 * 获取当前工作区 ID
 * 缓存中必须有值，否则抛出异常
 */
export function getWorkspaceId() {
  if (!_cachedWorkspaceId) {
    throw new Error(
      '工作区 ID 未初始化，请先调用 fetchAndCacheWorkspaceId() 或 restoreWorkspaceId()'
    )
  }
  return _cachedWorkspaceId
}

/**
 * 获取当前用户的工作区信息
 * @returns {Promise<{workspaceId: string, [key: string]: any}>}
 */
export function getMyWorkspaceApi() {
  return agentRequest({
    url: '/agent-api/claw/api/v1/workspaces/my',
    method: 'GET',
    headers: {}
  })
}

/**
 * Fire-and-forget companion call: whenever the workspaces/my API is invoked,
 * also POST to the sphere admin users/add endpoint. No payload is needed;
 * auth is handled by clientRequest (portal Bearer token). Never blocks or
 * breaks the main flow; errors are logged only.
 */
function syncAddSphereUser() {
  try {
    addSphereUser({}).catch((err) => {
      console.warn('[syncAddSphereUser] sphere users/add sync failed:', err)
    })
  } catch (err) {
    console.warn('[syncAddSphereUser] trigger sphere users/add failed:', err)
  }
}

/**
 * 获取并缓存工作区 ID（应用初始化时调用一次即可）
 * @returns {Promise<string>}
 */
export async function fetchAndCacheWorkspaceId(forceRefresh = false) {
  if (!forceRefresh && _cachedWorkspaceId) return _cachedWorkspaceId
  // Fire the sphere users/add call together with the workspaces/my request
  // (no payload, never blocks the main flow).
  syncAddSphereUser()
  const res = await getMyWorkspaceApi()
  const data = res.data[0]
  const id = data && data.id
  if (id && id !== _cachedWorkspaceId) {
    _cachedWorkspaceId = id
    localStorage.setItem('claw_workspace_id', id)
  }
  return _cachedWorkspaceId
}

/**
 * 从 localStorage 恢复缓存的工作区 ID（页面刷新后快速恢复，避免重复请求）
 */
export function restoreWorkspaceId() {
  _cachedWorkspaceId = localStorage.getItem('claw_workspace_id') || null
  return _cachedWorkspaceId
}
// 响应拦截器：处理 401 自动刷新 token 并重试，统一错误提示
agentRequest.interceptors.response.use(
  (response) => {
    // Blob 响应（文件下载等）直接返回完整 response，不走业务码检查
    if (response.config && response.config.responseType === 'blob') {
      return response
    }
    // Text 响应（文件预览等）直接返回完整 response，不走业务码检查
    if (response.config && response.config.responseType === 'text') {
      return response
    }
    const res = response.data
    if (
      res.code === 200 ||
      res.code === '200' ||
      res.code === 0 ||
      res.status_code === 200
    ) {
      return res
    }
    // 业务错误码非 200，统一提示（silent 的后台请求不弹全局错误提示）
    const msg = getErrorMessage(res)
    if (!response.config || !response.config.silent) {
      Message.error(msg)
    }
    return Promise.reject(res)
  },
  (error) => {
    const originalRequest = error.config

    // 只对 401 且未重试过的请求做自动刷新
    if (error.response?.status === 401 && !originalRequest?._tianzhiRetry) {
      originalRequest._tianzhiRetry = true

      // 如果正在刷新 token，排队等待刷新完成
      if (_isRefreshingToken) {
        return new Promise((resolve) => {
          _pendingRequests.push((newToken) => {
            originalRequest.headers['Authorization'] = `${newToken}`
            resolve(agentRequest(originalRequest))
          })
        })
      }

      _isRefreshingToken = true
      return refreshTianzhiToken()
        .then((newToken) => {
          // 重试排队的请求
          _pendingRequests.forEach((cb) => cb(newToken))
          _pendingRequests = []
          // 重试当前请求
          originalRequest.headers['Authorization'] = `${newToken}`
          return agentRequest(originalRequest)
        })
        .catch((refreshErr) => {
          console.warn('[agentAuth] 刷新 tianzhiToken 失败:', refreshErr)
          _pendingRequests = []
          return Promise.reject(error)
        })
        .finally(() => {
          _isRefreshingToken = false
        })
    }

    // 非 401 错误，统一提示（silent 的后台请求不弹全局错误提示）
    if (
      (error.response?.status !== 401 || originalRequest?._tianzhiRetry) &&
      !originalRequest?.silent
    ) {
      const msg = getErrorMessage(error)
      Message.error(msg)
    }

    return Promise.reject(error)
  }
)

/**
 * 获取智能体平台的 RSA 公钥
 * @returns {Promise<{public_key: string}>}
 */
export function getPublicKeyApi() {
  return agentRequest({
    url: '/agent-api/api/v1/user/public_key',
    method: 'GET',
    headers: {
      ApiKey: 'Z0MsqugCI78t4voc9Q4ftdqtD4UdOuOY'
    }
  })
}

/**
 * 注册到智能体平台，获取 agent token
 * @param {string} password - RSA 加密后的密码
 * @param {string} eiipToken - 门户平台的 token（作为 third_token）
 * @returns {Promise<{access_token: string, refresh_token: string, third_user_name: string, platform_role?: string}>}
 */
export function newNewRegistAgentSystemApi(password, eiipToken) {
  return agentRequest({
    url: '/agent-api/api/v1/user/new_c_regist',
    method: 'POST',
    data: {
      user_name: '任务',
      password,
      captcha_key: '',
      third_token: eiipToken
    }
  })
}

/**
 * 获取 CosmoClaw 实例信息（用于 WebSocket 连接）
 * 使用 tianzhi agent token 鉴权
 * @returns {Promise<{id: string, ip: string, port: number, accessUrl?: string, token?: string}>}
 */
export function getClawInstanceApi() {
  return agentRequest({
    url: '/agent-api/intelligent-agent/api/v1/cosmo-claw/getCosmoClaw',
    method: 'GET'
  })
}
export function updateJobApi(data) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-jobs/${data.id}`,
    method: 'PUT',
    data,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function deleteJobApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-jobs/${id}`,
    method: 'DELETE',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function toggleJobApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-jobs/${params.id}/toggle`,
    method: 'PUT',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function runJobApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-jobs/${id}/run`,
    method: 'POST',
    // data,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobListApi(params) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/cron-jobs',
    method: 'GET',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
/**
 * 获取会话记录分页（最近工作记录）
 * @param {Object} params 查询参数（agentId / page / size 等）
 * @returns {Promise} 返回 records / total / pages / current / size
 */
export function getConversationPageApi(params) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/conversations/page',
    method: 'GET',
    params, headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

export function getFilesApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/conversations/${params.conversationId}/files`,
    method: 'GET',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getFilesUrlApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/conversations/${params.conversationId}/files/${params.fileId}/download`,
    method: 'GET',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function downloadFileApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/conversations/${params.conversationId}/files/${params.fileId}/download`,
    method: 'GET',
    responseType: 'blob',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getFilesPreviewApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/conversations/${params.conversationId}/files/${params.fileId}/download`,
    method: 'GET',
    responseType: 'text',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobPageApi(params) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/cron-jobs/page',
    method: 'GET',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobDetailApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-jobs/${id}`,
    method: 'GET',
    // data,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
/**
 * 获取数字人列表（用于任务创建时选择执行的数字人）
 * @returns {Promise}
 */
export function getAgentListApi() {
  return agentRequest({
    url: '/agent-api/claw/api/v1/agents',
    method: 'GET',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function createJobApi(data) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/cron-jobs',
    method: 'POST',
    data,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
/**
 * 查询定时任务推送渠道选项
 * @param {String|Number} agentId 执行数字人 ID
 * @returns {Promise} 返回 { type, label, available }[]；available=true 表示已配置且通讯实例匹配当前数字人
 */
export function getPushChannelOptionsApi(agentId) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/cron-jobs/push-channel-options',
    method: 'GET',
    params: { agentId },
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobHistoryListApi(params) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/cron-runs',
    method: 'GET',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobHistoryPageApi(params) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/cron-runs/page',
    method: 'GET',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getDecisionListApi(params) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/decisions/listByReviewer',
    method: 'GET',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getDecisionDetailApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/decisions/${id}`,
    method: 'GET',
    // params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobHistoryDetailApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-reports/by-run/${id}`,
    method: 'GET',
    // params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobReportDetailApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-reports/${id}`,
    method: 'GET',
    // params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobReportListApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-reports`,
    method: 'GET',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getJobReportPageApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-reports/page`,
    method: 'GET',
    params,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

export function deleteJobReportApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-reports/${id}`,
    method: 'DELETE',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

export function deleteJobHisReportApi(id) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-runs/${id}`,
    method: 'DELETE',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

/** 处置异常报告：PUT /claw/api/v1/cron-reports/{id}/anomaly/disposition */
export function disposeAnomalyApi(id, data) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/cron-reports/${id}/anomaly/disposition`,
    method: 'PUT',
    data,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}

export const getReportPageApi = getJobReportPageApi
export function reviewDecisionApi(id, data) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/decisions/${id}/review`,
    method: 'PUT',
    data,
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
export function getTenantMembersApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/tenant/members/page`,
    method: 'GET',
    params,
    headers: {
      // 'X-Workspace-Id': process.env.VUE_APP_WS_WORKSPACE_ID
    }
  })
}
export function getAgentPlazaApi(params) {
  return agentRequest({
    url: '/agent-api/intelligent-agent/api/v1/assistant/client/page',
    method: 'GET',
    params,
    headers: {}
  })
}

/**
 * 获取知识库客户端分页列表（用于构建知识库 iframe 嵌入地址）
 * @param {Object} params
 * @returns {Promise}
 */
export function getKnowledgeClientPageApi(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/agent/knowledge',
    method: 'GET',
    params
  })
}

/**
 * 查询数字人绑定的知识库列表
 * @param {Object} params
 * @param {String} params.agentId 数字人ID
 * @returns {Promise} data 为知识库数组（含 kbId / kbType / folderNum / docNum / chunkNum 等）
 */
export function getAgentKnowledgeListApi(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/agent/knowledge/list',
    method: 'GET',
    params
  })
}

export function getSchedules(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/sphere/feishu/schedules/month',
    method: 'GET',
    params
  })
}
/**
 * 获取数字人广场分类（sphere-admin 数字人租户分类接口）
 * @param {Object} params 可选额外参数
 * @returns {Promise}
 */
export function getAgentTenantCategoriesApi(params) {
  const workspaceId = getWorkspaceId()
  if (!workspaceId) {
    return Promise.reject(new Error('工作区ID未初始化'))
  }
  return clientRequest({
    url: '/sphere-admin/api/v1/agent/tenant/categories',
    method: 'GET',
    // TODO:  临时固定workspaceId 后面改为不传
    headers: { 'x-workspace-id': workspaceId },
    params
  })
}

/**
 * 获取数字人广场列表（全部 / 已订阅）
 * 兼容返回 { records, total, size, current, pages } 的分页结构
 * @param {Object} params 查询参数，如 { subscribed: 3, name, templateType, categoryId, subcategoryId, pageNum, pageSize }
 * @returns {Promise}
 */
export function getAgentTenantListApi(params) {
  const workspaceId = getWorkspaceId()
  if (!workspaceId) {
    return Promise.reject(new Error('工作区ID未初始化'))
  }
  return clientRequest({
    url: '/sphere-admin/api/v1/agent/tenant/list',
    method: 'GET',
    headers: { 'x-workspace-id': workspaceId },
    // 新版接口直接返回 records/total/size/current/pages 分页结构
    skipResponseCodeCheck: true,
    params
  })
}

/**
 * 获取智能体广场列表（sphere-admin 智能体市场客户端分页接口）
 * 仅返回已上架（status=1）智能体；支持名称模糊检索与市场分类检索
 * @param {Object} params 查询参数 { pageNum, pageSize, name, marketCategoryType, marketCategorySubtype }
 * @returns {Promise} 响应体为 { code, msg, total, rows }，rows 内字段：
 *   { id, assistantId, name, logo, desc, category, externalLinkUrl, deliverMode, marketCategoryType, marketCategorySubtype }
 */
export function getAssistantClientPageApi(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/sphere/assistant/client/page',
    method: 'GET',
    params
  })
}

export function getSchedulesArchive(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/sphere/feishu/schedules/archive',
    method: 'GET',
    params
  })
}
/**
 * 获取智能体广场市场一级分类（取已上架智能体的市场一级分类去重）
 * @returns {Promise} 响应体 data 为 [{ id, categoryName, sort }]
 */
export function getAssistantClientCategoriesApi(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/sphere/assistant/client/categories',
    method: 'GET',
    params
  })
}

export function getSchedulesDetail(params) {
  return clientRequest({
    url: `/sphere-admin/api/v1/sphere/feishu/schedules/${params.id}`,
    method: 'GET',
    params
  })
}

/**
 * 按名称分页搜索企业（会前准备-合作方联想）
 * GET /eiip-ai/enterprise-data/enterprise/page-by-name?keywords=xxx
 * @param {Object} params { keywords: 企业名称关键词 }
 * @returns {Promise}
 */
export function searchEnterpriseByName(params) {
  return portalRequest({
    url: '/eiip-ai/enterprise-data/enterprise/page-by-name',
    method: 'GET',
    params
  })
}

/**
 * 查询会议会前准备详情
 * GET /eiip-ai/meeting-preparation?meetingSource=FEISHU&meetingId=xxx
 * @param {Object} params { meetingId, meetingSource }
 * @returns {Promise}
 */
export function getMeetingPreparation(params) {
  return portalRequest({
    url: '/eiip-ai/meeting-preparation',
    method: 'GET',
    params
  })
}

/**
 * 新增会议会前准备（详情中不存在 preparation.id 时）
 * POST /eiip-ai/meeting-preparation
 * @param {Object} data { partners, meetingId, meetingSource, attachments, meetingDescription }
 * @returns {Promise}
 */
export function saveMeetingPreparation(data) {
  return portalRequest({
    url: '/eiip-ai/meeting-preparation',
    method: 'POST',
    data
  })
}

/**
 * 编辑会议会前准备（详情中存在 preparation.id 时）
 * PUT /eiip-ai/meeting-preparation
 * @param {Object} data { id, partners, meetingId, meetingSource, attachments, meetingDescription }
 * @returns {Promise}
 */
export function updateMeetingPreparation(data) {
  return portalRequest({
    url: '/eiip-ai/meeting-preparation',
    method: 'PUT',
    data
  })
}

/**
 * 触发会前材料智能报告生成
 * POST /eiip-ai/meeting-preparation/report/generate
 * @param {Object} data { preparationId }
 * @returns {Promise}
 */
export function generateMeetingReport(data) {
  return portalRequest({
    url: '/eiip-ai/meeting-preparation/report/generate',
    method: 'POST',
    data
  })
}

export function getFileList(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/mateclaw/file/listFile',
    method: 'GET',
    params
  })
}

export function getFileDetail(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/mateclaw/file/getFileDetail',
    method: 'GET',
    params
  })
}

export function deleteFile(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/mateclaw/file/deleteFile',
    method: 'DELETE',
    params
  })
}

export function downloadFile(params) {
  return clientRequest({
    url: '/sphere-admin/api/v1/mateclaw/file/downloadFile',
    method: 'GET',
    params
  })
}

export function setAuthorizationApi(data) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/auth/setAuthorization',
    method: 'POST',
    data,
    // 后台鉴权同步请求：不阻塞页面、不弹错误提示，且快速失败（避免网络差时长时间 pending）
    silent: true,
    timeout: 8000,
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

export function getUserAgentsApi(params) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/agents/listByUser`,
    method: 'GET',
    params,
    headers: {
      // 'X-Workspace-Id': process.env.VUE_APP_WS_WORKSPACE_ID
    }
  })
}

/**
 * 批量删除会话
 * @param {string[]} conversationIds - 会话ID列表
 * @returns {Promise}
 */
export function batchDeleteConversations(conversationIds) {
  const workspaceId = getWorkspaceId()
  if (!workspaceId) {
    return Promise.reject(new Error('工作区ID未初始化'))
  }
  return agentRequest({
    url: '/agent-api/claw/api/v1/conversations/batch-delete',
    method: 'POST',
    headers: { 'x-workspace-id': workspaceId },
    data: { conversationIds }
  })
}

/**
 * 获取数字人/智能体的子菜单列表（按 agentId）
 * 用于左侧菜单数字人/智能体目录下子菜单的动态渲染
 * @param {string|number} agentId 数字人/智能体 ID
 * @returns {Promise} 响应体为 { code, msg, data: [{ id, agentId, menuName, resourceAddress, icon, sort, status, isDeleted }] }
 */
export function getAgentMenuListByAgentIdApi(agentId) {
  return clientRequest({
    url: `/sphere-admin/api/v1/sphere/agentMenu/listByAgentId/${agentId}`,
    method: 'GET'
  })
}

/**
 * 获取数字人对话初始页引导卡片列表（按 agentId）
 * 用于 DigitalHumanHome.vue 引导卡片的动态渲染
 * @param {string|number} agentId 数字人/智能体 ID
 * @returns {Promise} 响应体为 { code, msg, data: [{ id, agentId, entryName, icon, remark, command, sort, mockSwitch, isDeleted }] }
 * mockSwitch: 0-真实问题（走 sendMessage）, 1-mock 问题（走 mock trigger 流程）
 */
export function getAgentQuickEntryListByAgentIdApi(agentId) {
  return clientRequest({
    url: `/sphere-admin/api/v1/sphere/agentQuickEntry/listByAgentId/${agentId}`,
    method: 'GET'
  })
}

/**
 * 追加 mock 流程消息到会话记录（供历史回看恢复时使用）
 * @param {string} conversationId - 会话ID
 * @param {string} role - 'user' | 'assistant'
 * @param {string} content - 消息内容
 * @returns {Promise}
 */
export function appendMockMessageApi(conversationId, role, content, agentId) {
  const workspaceId = getWorkspaceId()
  if (!workspaceId) {
    return Promise.reject(new Error('工作区ID未初始化'))
  }
  return agentRequest({
    url: `/agent-api/claw/api/v1/conversations/${conversationId}/messages`,
    method: 'POST',
    headers: { 'x-workspace-id': workspaceId },
    data: { agentId, role, content }
  })
}

/**
 * JSON 卡片校验/修复（渲染前调用，由服务端校验并修复格式错误）
 * @param {string} cardJson 原始 JSON 卡片文本（可为非法 JSON、带 ```json 围栏、空串等）
 * @returns {Promise<{code:number, msg:string, data:{valid:boolean, cardJson:string, repairAttempts:number, errorMessage:string|null}}>}
 */
export function checkJsonCardApi(cardJson) {
  return agentRequest({
    url: '/agent-api/claw/api/v1/json-card/check',
    method: 'POST',
    headers: { 'X-Workspace-Id': getWorkspaceId() },
    data: { cardJson }
  })
}

/**
 * 获取数字人（agent）的模型列表（替代原 models.list websocket 调用）
 * @param {string|number} agentId 数字人 agent id
 * @returns {Promise<{code:number, msg:string, data:Array}>}
 * data：外层数组为 provider 分组（name=provider），extraModels 为模型列表（name=模型名称）
 */
export function getAgentModelsApi(agentId) {
  return agentRequest({
    url: `/agent-api/claw/api/v1/models/agent/${agentId}`,
    method: 'GET',
    headers: {
      'X-Workspace-Id': getWorkspaceId()
    }
  })
}
